import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigation } from 'react-router-dom';

/**
 * Thin progress strip that animates under the Topbar whenever a route change
 * is in flight. Two triggers:
 *   1. `useNavigation().state !== 'idle'` — Data Router loader/action in progress.
 *   2. Any `location.pathname` change — covers lazy-chunk loads (React Suspense)
 *      since Data Router doesn't report those as navigation state on its own.
 *
 * The bar auto-hides after both a short floor (so instant transitions still
 * blip visibly) and once the next pathname has settled.
 */
export default function RouteProgressBar() {
  const { state } = useNavigation();
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const fadeTimer = useRef(null);
  const minShow = useRef(null);

  // Data Router navigation state
  useEffect(() => {
    if (state !== 'idle') {
      setVisible(true);
      if (fadeTimer.current) { clearTimeout(fadeTimer.current); fadeTimer.current = null; }
    } else if (visible) {
      // Fade out shortly after idle
      if (fadeTimer.current) clearTimeout(fadeTimer.current);
      fadeTimer.current = setTimeout(() => setVisible(false), 140);
    }
    return () => { if (fadeTimer.current) { clearTimeout(fadeTimer.current); fadeTimer.current = null; } };
  }, [state, visible]);

  // Location-change signal (covers lazy-chunk / Suspense transitions)
  useEffect(() => {
    setVisible(true);
    if (minShow.current) clearTimeout(minShow.current);
    minShow.current = setTimeout(() => {
      // Only hide if we're idle — don't fight the state-driven effect above.
      if (state === 'idle') setVisible(false);
    }, 520);
    return () => { if (minShow.current) { clearTimeout(minShow.current); minShow.current = null; } };
    // We intentionally depend only on pathname + state idle transitions.
  }, [location.pathname, state]);

  return (
    <div
      data-testid="route-progress-bar"
      aria-hidden="true"
      className={`route-progress-bar${visible ? ' is-active' : ''}`}
    />
  );
}
